import React, { useState, useRef, useCallback, useEffect } from "react";
import { API_URL } from "../config";
import "./VideoGenerator.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

/* --- ImageModal --- */
function ImageModal({ src, label, onClose }) {
  if (!src) return null;
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
        <button className="cs-modal__close" onClick={onClose}>&times;</button>
        <img src={src} alt={label} className="cs-modal__image" />
        <span className="cs-modal__label">{label}</span>
      </div>
    </div>
  );
}

/* --- Tag chip --- */
function TagChip({ label, color, onRemove }) {
  return (
    <span className={`vg-tag vg-tag--${color}`}>
      @{label}
      {onRemove && <button className="vg-tag__remove" onClick={onRemove}>&times;</button>}
    </span>
  );
}

/* --- Video param selector --- */
function ParamSelect({ label, value, options, onChange }) {
  return (
    <div className="vg-param">
      <span className="vg-param__label">{label}</span>
      <select className="vg-param__select" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

/* --- Main page --- */
const VG_STORAGE_KEY = "720mt_videogen";

function loadVgSession() {
  try {
    const raw = localStorage.getItem(VG_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveVgSession(data) {
  try { localStorage.setItem(VG_STORAGE_KEY, JSON.stringify(data)); } catch {}
}

export default function VideoGenerator() {
  const headers = { Authorization: `Bearer ${getToken()}` };

  // Restore session
  const saved = useRef(loadVgSession());

  // Data sources
  const [characters, setCharacters] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [artifacts, setArtifacts] = useState([]);

  // Selections
  const [selectedSheets, setSelectedSheets] = useState(saved.current?.selectedSheets || []);
  const [selectedScene, setSelectedScene] = useState(saved.current?.selectedScene || null);
  const [selectedArtifacts, setSelectedArtifacts] = useState(saved.current?.selectedArtifacts || []);

  // Prompt editor
  const [prompt, setPrompt] = useState(saved.current?.prompt || "");
  const [generating, setGenerating] = useState(false);

  // Refine chat
  const [refineInput, setRefineInput] = useState("");
  const [refining, setRefining] = useState(false);
  const [chatHistory, setChatHistory] = useState(saved.current?.chatHistory || []);

  // Video params
  const [motor, setMotor] = useState(saved.current?.motor || "kling3");
  const [ratio, setRatio] = useState(saved.current?.ratio || "16:9");
  const [duration, setDuration] = useState(saved.current?.duration || "10");
  const [resolution, setResolution] = useState(saved.current?.resolution || "720p");
  const [soundFx, setSoundFx] = useState(saved.current?.soundFx ?? false);
  const [clips, setClips] = useState(saved.current?.clips || "1");

  // Video generation
  const [videoStatus, setVideoStatus] = useState("idle");
  const [videoUrl, setVideoUrl] = useState(saved.current?.videoUrl || null);
  const videoPollingRef = useRef(false);

  // Auto-save session
  useEffect(() => {
    saveVgSession({
      selectedSheets, selectedScene, selectedArtifacts,
      prompt, chatHistory,
      motor, ratio, duration, resolution, soundFx, clips,
      videoUrl,
    });
  }, [selectedSheets, selectedScene, selectedArtifacts, prompt, chatHistory, motor, ratio, duration, resolution, soundFx, clips, videoUrl]);

  // UI
  const [errorMsg, setErrorMsg] = useState("");
  const [modalSrc, setModalSrc] = useState(null);
  const [copied, setCopied] = useState(false);
  const promptRef = useRef(null);

  // Reel refs
  const charsReelRef = useRef(null);
  const scenesReelRef = useRef(null);
  const artifactsReelRef = useRef(null);
  const scrollReel = (ref, dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  const filterPlaceholders = (items) =>
    (items || []).filter((i) => !i.name?.includes(".emptyFolderPlaceholder"));

  // --- Load data ---
  const loadData = useCallback(async () => {
    try {
      // Characters
      const chRes = await fetch(`${API_URL}/characters`, { headers });
      const chData = await chRes.json();
      const chars = chData.characters || [];
      const detailed = await Promise.all(
        chars.map(async (c) => {
          const cid = c.id || c;
          try {
            const res = await fetch(`${API_URL}/characters/${cid}`, { headers });
            const detail = await res.json();
            const faceUrl = detail?.face?.reference?.[0]?.url || null;
            const sheets = filterPlaceholders(detail?.sheets || []);
            return { id: cid, displayName: detail.display_name || c.display_name || cid, faceUrl, sheets };
          } catch {
            return { id: cid, displayName: c.display_name || cid, faceUrl: null, sheets: [] };
          }
        })
      );
      setCharacters(detailed);

      // Scenes
      const scRes = await fetch(`${API_URL}/scenes`, { headers });
      const scData = await scRes.json();
      setScenes(filterPlaceholders(scData.backgrounds || []));

      // Artifacts
      const artRes = await fetch(`${API_URL}/artifacts`, { headers });
      const artData = await artRes.json();
      setArtifacts(filterPlaceholders(artData.artifacts || []));
    } catch {}
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // --- Selection handlers ---
  const toggleSheet = (char, sheet) => {
    const key = `${char.id}/${sheet.name}`;
    setSelectedSheets((prev) =>
      prev.find((s) => s.key === key)
        ? prev.filter((s) => s.key !== key)
        : [...prev, { key, charId: char.id, charName: char.displayName, sheetName: sheet.name, sheetUrl: sheet.url }]
    );
  };

  // Unique character names from selected sheets
  const selectedCharNames = [...new Set(selectedSheets.map((s) => s.charName))];

  const selectScene = (item) => {
    const path = `scenes/backgrounds/${item.name}`;
    setSelectedScene(selectedScene?.path === path ? null : { ...item, path });
  };

  const toggleArtifact = (item) => {
    setSelectedArtifacts((prev) =>
      prev.find((a) => a.name === item.name)
        ? prev.filter((a) => a.name !== item.name)
        : [...prev, item]
    );
  };

  // --- Insert @tag at cursor ---
  const insertTag = (tag) => {
    const ta = promptRef.current;
    if (!ta) {
      setPrompt((prev) => prev + ` @${tag} `);
      return;
    }
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = prompt;
    const before = text.substring(0, start);
    const after = text.substring(end);
    const newText = `${before}@${tag} ${after}`;
    setPrompt(newText);
    setTimeout(() => {
      ta.focus();
      const pos = start + tag.length + 2;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  // --- Generate cinematic prompt via Gemini ---
  const handleGeneratePrompt = async () => {
    setGenerating(true);
    setErrorMsg("");
    try {
      const res = await fetch(`${API_URL}/video/generate-prompt`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          characters: selectedCharNames,
          scene: selectedScene?.name?.replace(".png", "").replace(/_/g, " ") || "",
          artifacts: selectedArtifacts.map((a) => a.name.replace(".png", "").replace(/_/g, " ")),
          brief: prompt || "",
          motor,
          duration,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      setPrompt(data.prompt || "");
    } catch (err) {
      setErrorMsg(err.message);
    }
    setGenerating(false);
  };

  // --- Refine prompt via Gemini chat ---
  const handleRefine = async () => {
    if (!refineInput.trim()) return;
    setRefining(true);
    const userMsg = refineInput.trim();
    setRefineInput("");
    setChatHistory((prev) => [...prev, { role: "user", text: userMsg }]);
    try {
      const res = await fetch(`${API_URL}/video/refine-prompt`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          current_prompt: prompt,
          instruction: userMsg,
          characters: selectedCharNames,
        }),
      });
      if (!res.ok) throw new Error("Error al refinar");
      const data = await res.json();
      setPrompt(data.prompt || prompt);
      setChatHistory((prev) => [...prev, { role: "assistant", text: data.explanation || "Prompt actualizado." }]);
    } catch (err) {
      setChatHistory((prev) => [...prev, { role: "assistant", text: `Error: ${err.message}` }]);
    }
    setRefining(false);
  };

  // --- Generate video ---
  const handleGenerateVideo = async () => {
    if (!prompt) return;
    setVideoStatus("generating");
    setVideoUrl(null);
    setErrorMsg("");
    try {
      const res = await fetch(`${API_URL}/video/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({
          prompt,
          motor,
          ratio,
          duration,
          resolution,
          sound_fx: soundFx,
          clips: Number(clips),
          scene_url: selectedScene?.url || "",
          sheet_urls: selectedSheets.map((s) => s.sheetUrl),
          character_names: selectedCharNames,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      pollVideoStatus(data.job_id);
    } catch (err) {
      setErrorMsg(err.message);
      setVideoStatus("error");
    }
  };

  const pollVideoStatus = useCallback(async (jid) => {
    videoPollingRef.current = true;
    for (let i = 0; i < 200; i++) {
      if (!videoPollingRef.current) return;
      await new Promise((r) => setTimeout(r, 5000));
      try {
        const res = await fetch(
          `${API_URL}/video/${encodeURIComponent(jid)}/status`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        const data = await res.json();
        if (data.status === "completed") {
          setVideoUrl(data.video_url || null);
          setVideoStatus("done");
          videoPollingRef.current = false;
          return;
        }
        if (data.status === "error") {
          setErrorMsg(data.error || "Error al generar vídeo");
          setVideoStatus("error");
          videoPollingRef.current = false;
          return;
        }
      } catch (err) {
        console.error("Video poll error:", err);
      }
    }
    setErrorMsg("Tiempo agotado esperando el vídeo");
    setVideoStatus("error");
    videoPollingRef.current = false;
  }, []);

  // --- Copy prompt ---
  const handleCopy = () => {
    navigator.clipboard.writeText(prompt).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // --- Highlighted prompt preview ---
  const renderHighlightedPrompt = () => {
    if (!prompt) return null;
    const charNames = selectedCharNames;
    const allTags = [
      ...charNames.map((n) => ({ tag: n, color: "char" })),
      { tag: "location", color: "scene" },
      ...selectedArtifacts.map((a) => ({ tag: a.name.replace(".png", "").replace(/_/g, " "), color: "artifact" })),
      { tag: "Artifact", color: "artifact" },
    ];

    let result = prompt;
    const parts = [];
    let lastIndex = 0;

    // Simple regex to find @word patterns
    const regex = /@([\w\s-]+?)(?=[\s.,;:!?@]|$)/g;
    let match;
    while ((match = regex.exec(result)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: result.substring(lastIndex, match.index), type: "text" });
      }
      const tagName = match[1].trim();
      const tagInfo = allTags.find((t) => t.tag.toLowerCase() === tagName.toLowerCase());
      parts.push({ text: `@${tagName}`, type: tagInfo?.color || "text" });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < result.length) {
      parts.push({ text: result.substring(lastIndex), type: "text" });
    }

    return parts.map((p, i) =>
      p.type === "text" ? (
        <span key={i}>{p.text}</span>
      ) : (
        <span key={i} className={`vg-highlight vg-highlight--${p.type}`}>{p.text}</span>
      )
    );
  };

  const canGenerate = (selectedSheets.length > 0 || selectedScene) && !generating;
  const charCount = prompt.length;

  return (
    <div className="vg-page">
      {/* LEFT — Selectors + Prompt editor */}
      <div className="vg-left">
        {/* Header */}
        <div className="cs-result-header">
          <h2 className="cs-panel__title">Genera Video</h2>
          <div className="vg-header-actions">
            <button
              className={`cs-btn-generate ${generating ? "cs-btn-generate--loading" : ""}`}
              disabled={!canGenerate}
              onClick={handleGeneratePrompt}
            >
              {generating ? "Generando..." : "Generar Prompt"}
            </button>
            <button
              className={`vg-btn-generate-video ${videoStatus === "generating" ? "vg-btn-generate-video--loading" : ""}`}
              disabled={!prompt || videoStatus === "generating"}
              onClick={handleGenerateVideo}
            >
              {videoStatus === "generating" ? "Generando Video..." : "Generar Video"}
            </button>
          </div>
        </div>

        {/* Scene selector */}
        <section className="cs-section">
          <span className="cs-section__title">Escena</span>
          <div className="cs-reel">
            <button className="cs-reel__btn" onClick={() => scrollReel(scenesReelRef, -1)}>{"\u25C0"}</button>
            <div className="cs-reel__frames" ref={scenesReelRef}>
              {scenes.length > 0 ? scenes.map((item) => {
                const path = `scenes/backgrounds/${item.name}`;
                const isSel = selectedScene?.path === path;
                return (
                  <div
                    key={item.name}
                    className={`cs-reel-item cs-reel-item--wide vg-sel-item ${isSel ? "vg-sel-item--selected vg-sel-item--scene" : ""}`}
                    onClick={() => selectScene(item)}
                  >
                    <img src={item.url} alt={item.name} />
                    {isSel && <span className="vg-sel-item__check">&#10003;</span>}
                    <span className="cs-reel-item__name">{item.name.replace(".png", "").replace(/_/g, " ")}</span>
                  </div>
                );
              }) : <span className="cs-reel-empty">Sin escenarios — crea uno en Crea Escena</span>}
            </div>
            <button className="cs-reel__btn" onClick={() => scrollReel(scenesReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Character Sheets selector — all sheets grouped by character */}
        <section className="cs-section">
          <span className="cs-section__title">Personajes</span>
          <div className="cs-reel cs-reel--tall">
            <button className="cs-reel__btn" onClick={() => scrollReel(charsReelRef, -1)}>{"\u25C0"}</button>
            <div className="cs-reel__frames" ref={charsReelRef}>
              {characters.some((c) => c.sheets.length > 0) ? characters.filter((c) => c.sheets.length > 0).flatMap((c) =>
                c.sheets.map((sheet) => {
                  const key = `${c.id}/${sheet.name}`;
                  const isSel = selectedSheets.find((s) => s.key === key);
                  return (
                    <div
                      key={key}
                      className={`cs-reel-item cs-reel-item--wide vg-sel-item ${isSel ? "vg-sel-item--selected vg-sel-item--char" : ""}`}
                      onClick={() => toggleSheet(c, sheet)}
                    >
                      <img src={sheet.url} alt={sheet.name} />
                      {isSel && <span className="vg-sel-item__check">&#10003;</span>}
                      <span className="cs-reel-item__name">{sheet.name.replace(".png", "").replace(/_/g, " ")}</span>
                    </div>
                  );
                })
              ) : <span className="cs-reel-empty">Sin sheets — genera una en Crea Hoja Personaje</span>}
            </div>
            <button className="cs-reel__btn" onClick={() => scrollReel(charsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Artifacts selector */}
        <section className="cs-section">
          <span className="cs-section__title">Artefactos</span>
          <div className="cs-reel">
            <button className="cs-reel__btn" onClick={() => scrollReel(artifactsReelRef, -1)}>{"\u25C0"}</button>
            <div className="cs-reel__frames" ref={artifactsReelRef}>
              {artifacts.length > 0 ? artifacts.map((item) => {
                const isSel = selectedArtifacts.find((a) => a.name === item.name);
                return (
                  <div
                    key={item.name}
                    className={`cs-reel-item vg-sel-item ${isSel ? "vg-sel-item--selected vg-sel-item--artifact" : ""}`}
                    onClick={() => toggleArtifact(item)}
                  >
                    <img src={item.url} alt={item.name} />
                    {isSel && <span className="vg-sel-item__check">&#10003;</span>}
                    <span className="cs-reel-item__name">{item.name.replace(".png", "").replace(/_/g, " ")}</span>
                  </div>
                );
              }) : <span className="cs-reel-empty">Sin artefactos</span>}
            </div>
            <button className="cs-reel__btn" onClick={() => scrollReel(artifactsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Selected tags */}
        {(selectedSheets.length > 0 || selectedScene || selectedArtifacts.length > 0) && (
          <div className="vg-tags-bar">
            {selectedScene && (
              <TagChip label="location" color="scene" onRemove={() => setSelectedScene(null)} />
            )}
            {selectedSheets.map((s) => (
              <TagChip
                key={s.key}
                label={s.sheetName.replace(".png", "").replace(/_/g, " ")}
                color="char"
                onRemove={() => setSelectedSheets((prev) => prev.filter((x) => x.key !== s.key))}
              />
            ))}
            {selectedArtifacts.map((a) => (
              <TagChip
                key={a.name}
                label={a.name.replace(".png", "").replace(/_/g, " ")}
                color="artifact"
                onRemove={() => setSelectedArtifacts((prev) => prev.filter((x) => x.name !== a.name))}
              />
            ))}
            <button className="vg-tags-bar__insert" onClick={() => {
              if (selectedScene) insertTag("location");
              selectedSheets.forEach((s) => insertTag(s.sheetName.replace(".png", "").replace(/_/g, " ")));
              selectedArtifacts.forEach((a) => insertTag(a.name.replace(".png", "").replace(/_/g, " ")));
            }} title="Insertar todos los tags en el prompt">+ Insertar tags</button>
          </div>
        )}

        {/* Prompt editor */}
        <div className="vg-prompt-editor">
          <div className="vg-prompt-editor__header">
            <span className="vg-prompt-editor__title">Editor de prompt</span>
            <span className="vg-prompt-editor__count">{charCount} / 10000</span>
          </div>
          <textarea
            ref={promptRef}
            className="vg-prompt-editor__textarea"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe la escena cinematográfica... Usa @nombre para personajes, @location para escenario, @Artifact para objetos. O pulsa 'Generar Prompt' para que la IA escriba uno."
            rows={8}
            maxLength={10000}
          />
          {/* Highlighted preview */}
          {prompt && (
            <div className="vg-prompt-editor__preview">
              {renderHighlightedPrompt()}
            </div>
          )}
          <div className="vg-prompt-editor__actions">
            <button className="vg-btn-copy" onClick={handleCopy} disabled={!prompt}>
              {copied ? "Copiado!" : "Copiar prompt"}
            </button>
          </div>
        </div>

        {/* Video parameters bar */}
        <div className="vg-params-bar">
          <ParamSelect label="Motor" value={motor} onChange={setMotor} options={[
            { value: "kling3", label: "Kling 3.0 Omni" },
            { value: "seedance2", label: "Seedance 2.0" },
          ]} />
          <ParamSelect label="Ratio" value={ratio} onChange={setRatio} options={[
            { value: "16:9", label: "16:9" },
            { value: "9:16", label: "9:16" },
            { value: "1:1", label: "1:1" },
          ]} />
          <ParamSelect label="Duración" value={duration} onChange={setDuration} options={[
            { value: "5", label: "5s" },
            { value: "10", label: "10s" },
          ]} />
          <ParamSelect label="Resolución" value={resolution} onChange={setResolution} options={[
            { value: "720p", label: "720p" },
            { value: "1080p", label: "1080p" },
          ]} />
          <div className="vg-param">
            <span className="vg-param__label">Clips</span>
            <div className="vg-clips-control">
              <button onClick={() => setClips((p) => String(Math.max(1, Number(p) - 1)))}>-</button>
              <span>x{clips}</span>
              <button onClick={() => setClips((p) => String(Math.min(5, Number(p) + 1)))}>+</button>
            </div>
          </div>
          <div className="vg-param">
            <span className="vg-param__label">Sonido</span>
            <button
              className={`vg-toggle ${soundFx ? "vg-toggle--on" : ""}`}
              onClick={() => setSoundFx(!soundFx)}
            >
              {soundFx ? "ON" : "OFF"}
            </button>
          </div>
        </div>

        {errorMsg && <div className="cs-error">{errorMsg}</div>}
      </div>

      {/* RIGHT — Video preview + Refine chat */}
      <div className="vg-right">
        {/* Video preview */}
        <div className="vg-video-preview">
          <h2 className="cs-panel__title">Video</h2>
          <div className="vg-video-preview__container">
            {videoStatus === "idle" && !videoUrl && (
              <div className="vg-video-preview__empty">
                <span className="vg-video-preview__icon">&#9654;</span>
                <p>Genera un prompt y pulsa Generar Video</p>
              </div>
            )}
            {videoStatus === "generating" && (
              <div className="vg-video-preview__empty">
                <span className="cs-spinner" />
                <p>Generando vídeo...</p>
              </div>
            )}
            {videoStatus === "done" && videoUrl && (
              <div className="vg-video-preview__player">
                <video controls autoPlay loop className="vg-video-preview__video">
                  <source src={videoUrl} type="video/mp4" />
                </video>
                <div className="vg-video-preview__actions">
                  <a href={videoUrl} download="generated_video.mp4" className="vg-btn-download">Descargar</a>
                </div>
              </div>
            )}
            {videoStatus === "error" && (
              <div className="vg-video-preview__empty">
                <p className="cs-error" style={{ margin: 0 }}>{errorMsg || "Error al generar"}</p>
              </div>
            )}
          </div>
        </div>

        {/* Refine chat */}
        <h2 className="cs-panel__title" style={{ marginTop: 8 }}>Refinar prompt</h2>
        <div className="vg-chat">
          <div className="vg-chat__messages">
            {chatHistory.length === 0 && (
              <div className="vg-chat__empty">
                <span className="vg-chat__empty-icon">&#9998;</span>
                <p>Pregúntame lo que quieras sobre tu prompt</p>
              </div>
            )}
            {chatHistory.map((msg, i) => (
              <div key={i} className={`vg-chat__msg vg-chat__msg--${msg.role}`}>
                {msg.text}
              </div>
            ))}
            {refining && <div className="vg-chat__msg vg-chat__msg--assistant vg-chat__msg--loading">Pensando...</div>}
          </div>
          <div className="vg-chat__input-bar">
            <button
              className="vg-chat__quick-btn"
              onClick={() => { setRefineInput("Mejorar prompt"); }}
            >
              Mejorar prompt
            </button>
            <div className="vg-chat__input-wrap">
              <input
                className="vg-chat__input"
                value={refineInput}
                onChange={(e) => setRefineInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleRefine(); } }}
                placeholder="Añade detalles y efectos de iluminación..."
                disabled={refining || !prompt}
              />
              <button
                className="vg-chat__send"
                onClick={handleRefine}
                disabled={refining || !refineInput.trim() || !prompt}
              >
                &#x2191;
              </button>
            </div>
          </div>
        </div>
      </div>

      {modalSrc && <ImageModal src={modalSrc} label="Preview" onClose={() => setModalSrc(null)} />}
    </div>
  );
}
